import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  SERVICE_MODEL_COLUMNS,
  buildSbsPreviewRows,
  formatPreviewValue,
  parsePreviewValue,
} from './sbsExcel';

function formatServiceDate(value) {
  const text = String(value || '');
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text || '—';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function EditableCell({
  value,
  editable,
  isEditing,
  draft,
  onStartEdit,
  onDraftChange,
  onCommit,
  onCancel,
  align = 'right',
}) {
  const hoverBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const editBg = useColorModeValue('yellow.50', 'whiteAlpha.200');

  if (!editable) {
    return (
      <Text fontSize="sm" textAlign={align} color="gray.400">
        —
      </Text>
    );
  }

  if (isEditing) {
    return (
      <Input
        size="sm"
        type="text"
        value={draft}
        autoFocus
        bg={editBg}
        textAlign={align}
        onChange={(e) => onDraftChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onCommit();
          if (e.key === 'Escape') onCancel();
        }}
      />
    );
  }

  return (
    <Box
      py={1}
      px={2}
      borderRadius="md"
      textAlign={align}
      cursor="pointer"
      onDoubleClick={onStartEdit}
      _hover={{ bg: hoverBg }}
      title="Doble clic para editar"
    >
      <Text fontSize="sm" fontFamily="mono">
        {formatPreviewValue(value)}
      </Text>
    </Box>
  );
}

export default function SbsEditablePreview({
  metrics,
  serviceByModel,
  services = [],
  monthLabel,
  omittedSbsRows = [],
  onMetricsChange,
  onServiceByModelChange,
  onRemoveSbsRow,
  onRemoveModelRow,
  onRemoveService,
}) {
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const headBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const sectionBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const previewRows = useMemo(
    () =>
      buildSbsPreviewRows(metrics).filter(
        (row) => !omittedSbsRows.includes(row.rowNum),
      ),
    [metrics, omittedSbsRows],
  );

  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const editingRef = useRef(null);

  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  const startEdit = useCallback((target, currentValue) => {
    setEditing(target);
    setDraft(currentValue == null || currentValue === '' ? '' : String(currentValue));
  }, []);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setDraft('');
  }, []);

  const commitMetricEdit = useCallback(
    (rowId, key) => {
      const parsed = parsePreviewValue(draft);
      onMetricsChange((prev) => ({ ...prev, [key]: parsed }));
      if (editingRef.current?.id === rowId) {
        setEditing(null);
        setDraft('');
      }
    },
    [draft, onMetricsChange],
  );

  const commitModelEdit = useCallback(
    (rowIndex, colKey) => {
      const parsed = parsePreviewValue(draft);
      onServiceByModelChange((prev) => {
        const next = prev.map((row, i) => {
          if (i !== rowIndex) return row;
          const updated = { ...row, [colKey]: parsed ?? 0 };
          if (colKey !== 'total') {
            updated.total = SERVICE_MODEL_COLUMNS.filter((c) => c !== 'total').reduce(
              (acc, k) => acc + (Number(updated[k]) || 0),
              0,
            );
          }
          return updated;
        });
        return next;
      });
      if (editingRef.current?.id === `model-${rowIndex}-${colKey}`) {
        setEditing(null);
        setDraft('');
      }
    },
    [draft, onServiceByModelChange],
  );

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="lg" overflow="hidden" bg="white">
      <Flex px={4} py={3} borderBottomWidth="1px" borderColor={border} align="center" justify="space-between">
        <Box>
          <Text fontWeight="semibold">Vista previa del Excel</Text>
          <Text fontSize="xs" color={muted}>
            Doble clic para editar un valor. Quitar saca la fila del Excel. En
            Servicios realizados, quitar un servicio recalcula las otras pestañas.
          </Text>
        </Box>
        <Text fontSize="sm" color={muted}>
          {monthLabel}
        </Text>
      </Flex>

      <Tabs colorScheme="blue" size="sm">
        <TabList px={4}>
          <Tab>Hoja SBS</Tab>
          <Tab>Servicio x Modelo</Tab>
          <Tab>Servicios realizados ({services.length})</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} py={0}>
            <TableContainer maxH="62vh" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} zIndex={1} bg={headBg}>
                  <Tr>
                    <Th w="55%">Concepto</Th>
                    <Th w="45%" isNumeric>
                      Valor
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {previewRows.map((row) => {
                    if (row.type === 'section') {
                      return (
                        <Tr key={row.id} bg={sectionBg}>
                          <Td py={2}>
                            <Text fontSize="xs" fontWeight="bold" letterSpacing="wide">
                              {row.label}
                            </Text>
                          </Td>
                          <Td textAlign="right">
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => onRemoveSbsRow?.(row.rowNum)}
                            >
                              Quitar
                            </Button>
                          </Td>
                        </Tr>
                      );
                    }
                    const cellId = row.id;
                    const isEditing = editing?.id === cellId;
                    return (
                      <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                        <Td pl={6}>
                          <Text fontSize="sm">{row.label}</Text>
                        </Td>
                        <Td isNumeric>
                          <Flex justify="flex-end" align="center" gap={2}>
                            <EditableCell
                              value={metrics?.[row.key]}
                              editable
                              isEditing={isEditing}
                              draft={draft}
                              onStartEdit={() => startEdit({ id: cellId }, metrics?.[row.key])}
                              onDraftChange={setDraft}
                              onCommit={() => commitMetricEdit(cellId, row.key)}
                              onCancel={cancelEdit}
                            />
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => onRemoveSbsRow?.(row.rowNum)}
                            >
                              Quitar
                            </Button>
                          </Flex>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel px={0} py={0}>
            <TableContainer maxH="62vh" overflowX="auto" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} zIndex={1} bg={headBg}>
                  <Tr>
                    <Th>Modelo</Th>
                    {SERVICE_MODEL_COLUMNS.map((col) => (
                      <Th key={col} isNumeric>
                        {col === 'total' ? 'TOTAL' : col}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {(serviceByModel || []).map((row, rowIndex) => (
                    <Tr key={row.model || rowIndex}>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Text fontSize="sm" fontWeight="medium">
                            {row.model}
                          </Text>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => onRemoveModelRow?.(rowIndex)}
                          >
                            Quitar
                          </Button>
                        </Flex>
                      </Td>
                      {SERVICE_MODEL_COLUMNS.map((colKey) => {
                        const cellId = `model-${rowIndex}-${colKey}`;
                        const isEditing = editing?.id === cellId;
                        return (
                          <Td key={colKey} isNumeric>
                            <EditableCell
                              value={row[colKey]}
                              editable
                              isEditing={isEditing}
                              draft={draft}
                              onStartEdit={() => startEdit({ id: cellId }, row[colKey])}
                              onDraftChange={setDraft}
                              onCommit={() => commitModelEdit(rowIndex, colKey)}
                              onCancel={cancelEdit}
                            />
                          </Td>
                        );
                      })}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel px={0} py={0}>
            <TableContainer maxH="62vh" overflowX="auto" overflowY="auto">
              <Table size="sm" variant="simple" sx={{ tableLayout: 'fixed' }} w="100%">
                <Thead position="sticky" top={0} zIndex={1} bg={headBg}>
                  <Tr>
                    <Th w="96px">Fecha</Th>
                    <Th w="100px">Matrícula</Th>
                    <Th w="160px">Cliente</Th>
                    <Th w="120px">Modelo</Th>
                    <Th w="220px">Servicio</Th>
                    <Th isNumeric>Horas</Th>
                    <Th isNumeric>Mano de obra</Th>
                    <Th isNumeric>Repuestos</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {services.map((line) => (
                    <Tr key={line.id}>
                      <Td whiteSpace="nowrap">{formatServiceDate(line.date)}</Td>
                      <Td>{line.plate || '—'}</Td>
                      <Td>{line.customer || '—'}</Td>
                      <Td>{line.model || '—'}</Td>
                      <Td whiteSpace="normal" wordBreak="break-word">
                        {line.serviceName || '—'}
                      </Td>
                      <Td isNumeric>{formatPreviewValue(line.productiveHours)}</Td>
                      <Td isNumeric>{formatPreviewValue(line.laborSales)}</Td>
                      <Td isNumeric>{formatPreviewValue(line.partsSales)}</Td>
                      <Td>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => onRemoveService?.(line.id)}
                        >
                          Quitar
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                  {!services.length && (
                    <Tr>
                      <Td colSpan={9}>
                        <Text fontSize="sm" color={muted} py={4} textAlign="center">
                          No hay servicios en este mes.
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
